import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { useAuth } from "src/context/AuthContext";
import { eligibleCompanies } from "src/utils/hallinta/companyRoles";
import { getCounts } from "src/utils/valut/valutApi";
import { providerApi } from "src/utils/tarjouspyynnot/providerApi";
import { captureError } from "src/utils/errorReporting";
import { deriveRole, readStamp } from "src/utils/nav/navState";

const SENT_STATUS_KEYS = ["open", "pending_verification", "accepted", "cancelled", "expired", "no_supply"];

const DEFAULT = {
  role: "anon",
  loading: false,
  providerCounts: null,
  customerCounts: null,
  hasSentRequests: false,
  hasActiveOrders: false,
};

const NavSignalsContext = createContext(DEFAULT);

/**
 * Single source of truth for nav role + counts. Runs the two existing probes
 * once (not per-consumer) so the app bar and the home resolver agree.
 *
 * Each probe result is STAMPED with the token it was fetched for — see
 * readStamp(). A token swap therefore invalidates both stamps in the SAME
 * render the identity changes, so `loading` flips true and consumers wait
 * instead of routing. Without the stamp the previous identity's counts stayed
 * live for one render with `loading` already false (both settled flags were
 * still true from the old session), and HomeResolver routed the new user by the
 * OLD user's role: a staffer impersonating a customer was sent to the
 * operator-only /tarjouspyynnot and left there.
 *
 * Re-probes on navigation keep the current stamp until they land, so they no
 * longer briefly re-show the `/` spinner either.
 *
 * Both effects keep all setState INSIDE an inner async run() — the codebase's
 * react-hooks/set-state-in-effect workaround (see the original HeaderActions /
 * MyValut pattern).
 */
export function NavSignalsProvider({ children }) {
  const { user, isAuthenticated, token } = useAuth();
  const location = useLocation();
  // { token, counts } | null — `counts: null` is a settled result (anon, probe
  // error, or not-a-provider), distinct from "no stamp for this token yet".
  const [customer, setCustomer] = useState(null);
  const [provider, setProvider] = useState(null);
  const maybeProvider = eligibleCompanies(user).length > 0;

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!isAuthenticated || !token) {
        if (!cancelled) setCustomer({ token, counts: null });
        return;
      }
      try {
        const counts = await getCounts({ token });
        if (!cancelled) setCustomer({ token, counts });
      } catch (err) {
        if (!cancelled) setCustomer({ token, counts: null });
        if (err.status !== 401) captureError(err, { tags: { entity: "pumppuRequest", operation: "nav-customer-counts" } });
      }
    }
    run();
    return () => { cancelled = true; };
  }, [isAuthenticated, token, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!isAuthenticated || !token || !maybeProvider) {
        if (!cancelled) setProvider({ token, counts: null });
        return;
      }
      try {
        const counts = await providerApi(token).getProviderCounts();
        if (!cancelled) setProvider({ token, counts });
      } catch (err) {
        if (!cancelled) setProvider({ token, counts: null });
        if (err.status !== 401 && err.status !== 403) captureError(err, { tags: { entity: "pumppuRequest", operation: "nav-provider-counts" } });
      }
    }
    run();
    return () => { cancelled = true; };
  }, [isAuthenticated, token, maybeProvider, location.pathname]);

  const value = useMemo(() => {
    const c = readStamp(customer, token);
    const p = readStamp(provider, token);
    const customerCounts = c.counts;
    const providerCounts = p.counts;
    const isOperator = !!providerCounts;
    const role = deriveRole({ isAuthenticated, isOperator });
    const jerryActive = !!providerCounts?.jerryActive;
    const operatorNeedsActivation = isOperator && !jerryActive;
    const applicationStatus = providerCounts?.application?.status ?? null;
    const hasSentRequests = SENT_STATUS_KEYS.reduce((acc, k) => acc + (customerCounts?.[k] ?? 0), 0) > 0;
    const hasActiveOrders =
      (customerCounts?.open ?? 0) + (customerCounts?.pending_verification ?? 0) + (customerCounts?.accepted ?? 0) > 0;
    const loading = isAuthenticated ? !(c.settled && p.settled) : false;
    return { role, loading, providerCounts, customerCounts, hasSentRequests, hasActiveOrders, jerryActive, operatorNeedsActivation, applicationStatus };
  }, [isAuthenticated, token, customer, provider]);

  return <NavSignalsContext.Provider value={value}>{children}</NavSignalsContext.Provider>;
}

export function useNavSignals() {
  return useContext(NavSignalsContext);
}
